import React from "react";
import { Grid, MenuItem, Typography } from "@mui/material";
import { IndexedCardInfo } from "../../../atoms/indexed-card-info";
import { StatusChip } from "../../../StatusChip/StatusChip";
import { RHFTextField } from "../../../atoms/form-fields/RHFTextField";
import { DatePicker } from "../../../DatePicker";
import { isOver18YearsOld } from "../../../shared/date";
import { isValidPostcode } from "../../AddressPicker/helpers/isValidPostcode";
import { RHFSelectField } from "../../../atoms/form-fields/RHFSelectField";
import { useFormContext } from "react-hook-form";

interface PersonalDetailsProps {
  cardIndex: string;
}

export const PersonalDetails = ({ cardIndex }: PersonalDetailsProps) => {
  const { watch } = useFormContext();
  const personal_building_no = watch("personal_building_no");
  const personal_building_name = watch("personal_building_name");
  const personal_address_more_than_3_years_old = watch(
    "personal_address_more_than_3_years_old"
  );
  const personal_previous_building_no = watch("personal_previous_building_no");
  const personal_previous_building_name = watch(
    "personal_previous_building_name"
  );
  return (
    <IndexedCardInfo index={cardIndex} title="Personal Details">
      <Grid container spacing={2}>
        <Grid item sm={6}>
          <RHFTextField
            label="First Name"
            name="personal_first_name"
            rules={{ required: true }}
          />
        </Grid>
        <Grid item sm={6}>
          <RHFTextField label="Middle Name" name="personal_middle_name" />
        </Grid>
        <Grid item sm={6}>
          <RHFTextField
            label="Last Name"
            name="personal_last_name"
            rules={{ required: true }}
          />
        </Grid>
        <Grid item sm={6}>
          <DatePicker
            name="personal_birthdate"
            label={"Date Of Birth"}
            disableFuture
            margin="none"
            rules={{
              required: true,
              validate: (val: Date) => {
                if (!isOver18YearsOld(val)) {
                  return "Must be over 18 years of age";
                }
                if (val.getFullYear() < 1900) {
                  return "Minimum date must be greater than 01/01/1900";
                }
                return true;
              },
            }}
          />
        </Grid>
        <Grid item sm={12}>
          <Typography variant="h4">Home Address</Typography>
        </Grid>
        <Grid item sm={12}>
          <StatusChip
            type="info"
            message="This must be the business owner’s home address."
          />
        </Grid>
        <Grid item sm={6}>
          <RHFTextField
            label="Flat / Apartment / Unit No"
            name="personal_apartment_no"
          />
        </Grid>
        <Grid item sm={6}>
          <RHFTextField
            label="Building Number"
            name="personal_building_no"
            rules={{ required: !personal_building_name }}
          />
        </Grid>
        <Grid item sm={6}>
          <RHFTextField
            label="Building Name"
            name="personal_building_name"
            rules={{ required: !personal_building_no }}
          />
        </Grid>
        <Grid item sm={6}>
          <RHFTextField
            label="Street"
            name="personal_street"
            rules={{ required: true }}
          />
        </Grid>
        <Grid item sm={6}>
          <RHFTextField
            label="City"
            name="personal_city"
            rules={{ required: true }}
          />
        </Grid>
        <Grid item sm={6}>
          <RHFTextField
            label="Postcode"
            name="personal_postcode"
            rules={{
              required: true,
              validate: (value) =>
                isValidPostcode(value) ? true : "Must be a valid postcode",
            }}
          />
        </Grid>
        <Grid item sm={6}>
          <RHFSelectField
            label="Have been at address for over 3 years?"
            name="personal_address_more_than_3_years_old"
            rules={{ required: true }}
          >
            <MenuItem value="Yes">Yes</MenuItem>
            <MenuItem value="No">No</MenuItem>
          </RHFSelectField>
        </Grid>
        {personal_address_more_than_3_years_old === "No" && (
          <>
            <Grid item sm={12}>
              <Typography variant="h4">Previous Home Address</Typography>
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="Flat / Apartment / Unit No"
                name="personal_previous_apartment_no"
              />
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="Building Number"
                name="personal_previous_building_no"
                rules={{ required: !personal_previous_building_name }}
              />
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="Building Name"
                name="personal_previous_building_name"
                rules={{ required: !personal_previous_building_no }}
              />
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="Street"
                name="personal_previous_street"
                rules={{ required: true }}
              />
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="City"
                name="personal_previous_city"
                rules={{ required: true }}
              />
            </Grid>
            <Grid item sm={6}>
              <RHFTextField
                label="Postcode"
                name="personal_previous_postcode"
                rules={{
                  required: true,
                  validate: (value) =>
                    isValidPostcode(value) ? true : "Must be a valid postcode",
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
    </IndexedCardInfo>
  );
};
